import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components/macro";

import { mailtoHref, telHref } from "../../utils/textUtils";

const ContactDetailsContainer = styled.div`
  padding: 8px;
  border-radius: 8px;
`;

const ContactSection = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ContactTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: var(--juno-font-size-h4-sm);
  color: var(--juno-color-purple);
`;

const ContactInfo = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 8px;
  font-size: 14px;
  line-height: 1.5;
`;

const Label = styled.span`
  font-weight: var(--juno-font-weight-bold);
  color: var(--juno-color-text-secondary);
`;

const Value = styled.span`
  color: var(--juno-color-text);
  overflow-wrap: anywhere;
`;

const ContactLink = styled.a`
  color: var(--juno-color-link);
`;

// Splits at the "@" by index because a lookbehind regex would blank the app on Safari
// below 16.4 (see frontend/AGENTS.md).
const emailWrapParts = (email) => {
  const afterAt = email.indexOf("@") + 1;
  return [email.slice(0, afterAt), email.slice(afterAt)].flatMap((half) => half.split(/(?=\.)/));
};

// Shown as typed; left as plain text when it is not one address. On a narrow screen a long
// address wraps after the "@" or before a "." rather than mid-word.
const EmailValue = ({ email }) => {
  const href = mailtoHref(email);
  if (!href) {
    return <Value>{email}</Value>;
  }
  return (
    <Value>
      <ContactLink href={href}>
        {emailWrapParts(email).map((part, i) => (
          <React.Fragment key={i}>
            {i > 0 && <wbr />}
            {part}
          </React.Fragment>
        ))}
      </ContactLink>
    </Value>
  );
};

EmailValue.propTypes = {
  email: PropTypes.string.isRequired,
};

// Shown as typed; left as plain text when it is not one dialable number.
const PhoneValue = ({ phone }) => {
  const href = telHref(phone);
  return <Value>{href ? <ContactLink href={href}>{phone}</ContactLink> : phone}</Value>;
};

PhoneValue.propTypes = {
  phone: PropTypes.string.isRequired,
};

function ContactDetails({ caseData, caseType }) {
  const otherSide = caseData.other_side;
  const mortgageBroker = caseData.mortgage_broker;
  const solicitorTitle = caseType === "buying" ? "Seller's solicitor" : "Buyer's solicitor";

  const hasSolicitorContent =
    otherSide &&
    (otherSide.firm_name ||
      otherSide.contact_person ||
      otherSide.email ||
      otherSide.phone ||
      otherSide.their_reference);

  const hasBrokerContent =
    mortgageBroker && (mortgageBroker.name || mortgageBroker.company || mortgageBroker.email || mortgageBroker.phone);

  return (
    <ContactDetailsContainer>
      {hasSolicitorContent && (
        <ContactSection>
          <ContactTitle>{solicitorTitle}</ContactTitle>
          <ContactInfo>
            {otherSide.firm_name && (
              <>
                <Label>Firm name:</Label>
                <Value>{otherSide.firm_name}</Value>
              </>
            )}

            {otherSide.contact_person && (
              <>
                <Label>Contact person:</Label>
                <Value>{otherSide.contact_person}</Value>
              </>
            )}

            {otherSide.email && (
              <>
                <Label>Email:</Label>
                <EmailValue email={otherSide.email} />
              </>
            )}

            {otherSide.phone && (
              <>
                <Label>Phone:</Label>
                <PhoneValue phone={otherSide.phone} />
              </>
            )}

            {otherSide.their_reference && (
              <>
                <Label>Their reference:</Label>
                <Value>{otherSide.their_reference}</Value>
              </>
            )}
          </ContactInfo>
        </ContactSection>
      )}

      {hasBrokerContent && (
        <ContactSection>
          <ContactTitle>Mortgage broker</ContactTitle>
          <ContactInfo>
            {mortgageBroker.name && (
              <>
                <Label>Name:</Label>
                <Value>{mortgageBroker.name}</Value>
              </>
            )}

            {mortgageBroker.company && (
              <>
                <Label>Company:</Label>
                <Value>{mortgageBroker.company}</Value>
              </>
            )}

            {mortgageBroker.email && (
              <>
                <Label>Email:</Label>
                <EmailValue email={mortgageBroker.email} />
              </>
            )}

            {mortgageBroker.phone && (
              <>
                <Label>Phone:</Label>
                <PhoneValue phone={mortgageBroker.phone} />
              </>
            )}
          </ContactInfo>
        </ContactSection>
      )}
    </ContactDetailsContainer>
  );
}
ContactDetails.propTypes = {
  caseData: PropTypes.shape({
    other_side: PropTypes.shape({
      firm_name: PropTypes.string,
      contact_person: PropTypes.string,
      email: PropTypes.string,
      phone: PropTypes.string,
      their_reference: PropTypes.string,
    }),
    mortgage_broker: PropTypes.shape({
      name: PropTypes.string,
      company: PropTypes.string,
      email: PropTypes.string,
      phone: PropTypes.string,
    }),
  }),
  caseType: PropTypes.string.isRequired,
};

export default ContactDetails;
